import { calculateIncrease } from "@pages/AdsAnalitics/components/Campaigns/utils";
import { AdsSalesApi } from "./ads-sales/ads-sales.api";
import { WidgetMetricKey, WidgetMetrics } from "./math/math.service";
import { IWidget } from "@pages/Dashboard/types/dashboard.types";
import { WIDGET_METRICS_CONFIG } from "./math/math.const";
import { SalesPerformanceService } from "./sales-performance/sales-performance.service";
import { OrganicApi } from "./organic/organic.api";
import { CfoApi } from "./cfo/cfo.api";
import { TotalSalesApi } from "./total-sales/total-sales.api";
import { fetcherMap } from "@components/CampaignTable/utils";
import { TypeTab } from "@pages/AdsAnalitics/components/Campaigns/components/WhatIfCharts/Tabs";

export interface ChartData {
  date: string;
  value: number;
}

interface ExtendedWidgetData extends IWidget {
  chartData?: ChartData[];
  whatIfValue: number;
  isWhatIf?: boolean;
}

interface GetWidgetsDataParams {
  selectedKeys?: WidgetMetricKey[];
  dateStart?: string;
  dateEnd?: string;
  compareStart?: string;
  compareEnd?: string;
  compareType?: "raw" | "percent";
  includeChartData?: boolean;
  saturation?: number;
  saturationKeys?: string[];
  asin?: string;
  tab?: TypeTab;
}

interface GetWidgetsMetricsParams {
  dateStart?: string;
  dateEnd?: string;
  compareStart?: string;
  compareEnd?: string;
  saturation?: number;
  saturationKeys?: string[];
  asin?: string;
  tab?: TypeTab;
  isWhatIf?: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");
const isoLocal = (d?: Date | string) => {
  let dt: Date;
  if (!d) {
    dt = new Date();
  } else if (typeof d === "string") {
    dt = new Date(d);
  } else {
    dt = d;
  }
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const CACHE_TTL = 60000;

export class WidgetClass {
  private readonly ads = new AdsSalesApi();
  private readonly org = new OrganicApi();
  private readonly cfo = new CfoApi();
  private readonly tot = new TotalSalesApi();
  private readonly perf = new SalesPerformanceService();

  private static readonly cache = new Map<
    string,
    Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }
  >();
  private static readonly inFlight = new Map<
    string,
    Promise<Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }>
  >();

  private cacheKey(params: GetWidgetsMetricsParams) {
    return JSON.stringify({
      start: isoLocal(params.dateStart),
      end: isoLocal(params.dateEnd),
      sat: params.saturation ?? 0,
      asin: params.asin ?? "",
      tab: params.tab ?? "Campaigns",
      whatIf: params.isWhatIf ?? false,
      sk: params.saturationKeys?.join(",") ?? "",
    });
  }

  public async getWidgetsData(
    selectedKeysOrParams: WidgetMetricKey[] | GetWidgetsDataParams,
    dateStart?: string,
    dateEnd?: string,
    compareStart?: string,
    compareEnd?: string,
    compareType: "raw" | "percent" = "raw",
    includeChartData = false,
    isWhatIf = false,
    saturation = 0,
    tab: TypeTab = "Campaigns",
    saturationKeys?: string[],
    asin?: string,
  ): Promise<ExtendedWidgetData[]> {
    if (Array.isArray(selectedKeysOrParams)) {
      const legacy: GetWidgetsDataParams = {
        selectedKeys: selectedKeysOrParams,
        dateStart,
        dateEnd,
        compareStart,
        compareEnd,
        compareType,
        includeChartData,
        saturation,
        saturationKeys,
        asin,
        tab,
      };
      return this.getWidgetsData(legacy);
    }

    const p = selectedKeysOrParams as GetWidgetsDataParams;
    const isWhatIfMode = (p.saturation ?? 0) > 0;

    const core = await this.getWidgetsMetrics({
      dateStart: p.dateStart,
      dateEnd: p.dateEnd,
      compareStart: p.compareStart,
      compareEnd: p.compareEnd,
      saturation: p.saturation,
      saturationKeys: p.saturationKeys,
      asin: p.asin,
      tab: p.tab ?? "Campaigns",
      isWhatIf: isWhatIfMode,
    });

    const charts = p.includeChartData
      ? await this.loadCharts(
          p.selectedKeys!,
          new Date(p.dateStart ?? isoLocal()),
          new Date(p.dateEnd ?? isoLocal()),
        )
      : [];

    return p.selectedKeys!.map((key, i) => {
      const { value, diff } = this.getMetricValue(
        core,
        key,
        p.compareType ?? "raw",
      );

      return {
        id: key,
        name: WidgetClass.name(key),
        value: String(value),
        originalValue: value,
        difference: isWhatIfMode ? "0" : diff,
        key,
        chartData: p.includeChartData ? charts[i] : undefined,
        whatIfValue: value,
        isWhatIf: isWhatIfMode,
      };
    });
  }

  public async getWidgetsMetrics(
    params: GetWidgetsMetricsParams,
  ): Promise<Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }> {
    const k = this.cacheKey(params);

    if (WidgetClass.cache.has(k)) {
      return WidgetClass.cache.get(k) as Partial<WidgetMetrics> & {
        compare?: Partial<WidgetMetrics>;
      };
    }

    if (WidgetClass.inFlight.has(k)) {
      return WidgetClass.inFlight.get(k) as Promise<
        Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }
      >;
    }

    const flight = this.fetchMetrics(params)
      .then((res) => {
        WidgetClass.cache.set(k, res);
        setTimeout(() => WidgetClass.cache.delete(k), CACHE_TTL);
        return res;
      })
      .finally(() => WidgetClass.inFlight.delete(k));

    WidgetClass.inFlight.set(k, flight);
    return flight;
  }

  private async fetchMetrics(
    params: GetWidgetsMetricsParams,
  ): Promise<Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }> {
    const start = params.dateStart ?? isoLocal();
    const end = params.dateEnd ?? isoLocal();
    const {
      compareStart,
      compareEnd,
      saturation = 0,
      saturationKeys = [],
      asin,
      tab = "Campaigns",
      isWhatIf = false,
    } = params;

    const adsFetch = isWhatIf
      ? fetcherMap[tab].bind(
          this.ads,
          start,
          end,
          saturation,
          [],
          saturationKeys,
          true,
        )
      : this.ads.getAllMetrics.bind(
          this.ads,
          start,
          end,
          saturation,
          asin ?? undefined,
        );

    const [adsRaw, orgRaw, cfoRaw, totRaw] = await Promise.all([
      adsFetch().then((r: any) => (Array.isArray(r) ? r[0] : r)),
      this.org.getOrganicMetrics(start, end, asin).catch(() => ({})),
      this.cfo.getExecutiveSummary(start, end, asin).catch(() => ({})),
      this.tot.getAll(start, end, asin).catch(() => ({})),
    ]);

    const base = this.adaptApiResponse(
      { ...adsRaw, ...orgRaw, ...cfoRaw, ...totRaw },
      isWhatIf,
    );

    if (!isWhatIf && compareStart && compareEnd) {
      const cmpRaw = await this.ads.getWidgetsComparePromise(
        start,
        end,
        compareStart,
        compareEnd,
      );
      return { ...base, compare: this.adaptApiResponse(cmpRaw, false) };
    }

    return base;
  }

  private getMetricValue(
    metrics: Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> },
    key: WidgetMetricKey,
    type: "raw" | "percent",
  ) {
    const mk =
      key.startsWith("ad") && !(metrics as any)[key] ? key.slice(2) : key;
    const curr = ((metrics as any)[mk] as number) ?? 0;
    if (!metrics.compare) return { value: curr, diff: "0" };
    const prev = ((metrics.compare as any)[mk] as number) ?? 0;
    if (type === "raw") {
      const delta = curr - prev;
      return { value: curr, diff: delta > 0 ? `+${delta}` : `${delta}` };
    }
    return { value: curr, diff: calculateIncrease(curr, prev).increase };
  }

  private async loadCharts(
    keys: WidgetMetricKey[],
    startDate: Date,
    endDate: Date,
  ): Promise<ChartData[][]> {
    const s = isoLocal(startDate);
    const e = isoLocal(endDate);
    return Promise.all(
      keys.map(async (key) => {
        const mk = WIDGET_METRICS_CONFIG[key] ? key : this.camel(key);
        try {
          const series: ChartData[] = await this.perf.fetchMetricData(mk, s, e);
          return series;
        } catch {
          return [];
        }
      }),
    );
  }

  private camel(s: string) {
    return s.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
  }

  private getMetricFromConfig(key: string) {
    return WIDGET_METRICS_CONFIG[key] ?? WIDGET_METRICS_CONFIG[this.camel(key)];
  }

  public static name(key: string) {
    return WIDGET_METRICS_CONFIG[key]?.label;
  }

  public formatValue(key: string, value: number): string {
    const cfg = this.getMetricFromConfig(key);
    if (key === "adCac") return this.formatAdCac(value);
    if (!cfg?.format) return this.formatNumber(value);
    return this.applyFormatter(cfg.format, value);
  }

  private formatAdCac(v: number) {
    return v < 0 ? `-$${(-v).toFixed(2)}` : `$${v.toFixed(2)}`;
  }

  private formatNumber(v: number) {
    return Math.floor(v).toLocaleString("en-US");
  }

  private applyFormatter(format: string, v: number) {
    const abs = Math.abs(v);
    const neg = v < 0;
    const fns: Record<string, (n: number, neg: boolean) => string> = {
      currency: (n, neg) =>
        `${neg ? "-" : ""}$${Math.floor(n).toLocaleString("en-US")}`,
      "percent-decimal": (n, neg) =>
        `${neg ? "-" : ""}${(n < 1 ? n * 100 : n).toFixed(2)}%`,
      percent: (n, neg) =>
        `${neg ? "-" : ""}${Math.floor(n < 1 ? n * 100 : n)}%`,
      number: (n, neg) =>
        `${neg ? "-" : ""}${Math.floor(n).toLocaleString("en-US")}`,
      decimal: (n, neg) =>
        `${neg ? "-" : ""}${n.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      "currency-decimal": (n, neg) =>
        `${neg ? "-" : ""}$${n.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    };
    const fn = fns[format] ?? fns.number;
    return fn(abs, neg);
  }

  private shouldIncludeKey(
    k: string,
    data: Record<string, any>,
    useWhatIf: boolean,
  ) {
    const isWhatIf = k.endsWith("_what_if");
    return useWhatIf ? isWhatIf || !(`${k}_what_if` in data) : !isWhatIf;
  }

  private normalizeKey(raw: string) {
    const base = raw.replace(/_what_if$/, "");
    const special: Record<string, string> = {
      roas: "adRoas",
      acos: "adAcos",
      cpc: "adCpc",
      cpm: "adCpm",
    };
    if (special[base]) return special[base];
    let norm = base.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
    if (base.startsWith("ad_"))
      norm = `ad${norm.charAt(2).toUpperCase()}${norm.slice(3)}`;
    return norm;
  }

  private adaptApiResponse(
    data: Record<string, any>,
    useWhatIf = false,
  ): Partial<WidgetMetrics> {
    if (!data) return {};
    return Object.entries(data).reduce((acc, [rawKey, val]) => {
      if (!this.shouldIncludeKey(rawKey, data, useWhatIf)) return acc;
      acc[this.normalizeKey(rawKey) as keyof WidgetMetrics] = val;
      return acc;
    }, {} as Partial<WidgetMetrics>);
  }
  public static convertKeyToName(key: string) {
    return WIDGET_METRICS_CONFIG[key]?.label;
  }
}

export const widgetService = new WidgetClass();
